<template>
  <div class="detail-content" data-testid="dynamic-program-detail-page">
    <Typography variant="h-1" as="h1" class="program-title">{{ programName }}</Typography>

    <div class="detail-body">
      <ProgramDetailColumn
        class="left-column"
        :image-url="imageUrl"
        :date-range-label="dateRangeLabel"
        :metric-rows="metricRows"
        :basis="basis"
        :is-dollar-budget="isDollarBudget"
        :metrics-loading="metricsLoading"
        :metrics-missing="metricsMissing"
        :status="status"
        :metrics-load-failed="metricsLoadFailed"
      />

      <v-card class="right-column" flat>
        <div v-if="showSubmitClaim" class="submit-claim">
          <Button data-testid="submit-claim-button" @click="$emit('submit-claim')">
            Submit Claim
          </Button>
        </div>
        <v-tabs v-model="activeTab" color="brandPrimary" class="tabs">
          <v-tab v-for="tab in tabs" :key="tab.key" data-testid="detail-tab">
            {{ tab.label }}
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="tabs-items">
          <v-tab-item v-for="tab in tabs" :key="tab.key">
            <div v-if="tab.bodyContent" class="tab-content">
              <Typography variant="h-4" as="h2">{{ tab.title }}</Typography>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div class="tab-content__body" v-html="sanitize(tab.bodyContent)"></div>
            </div>
            <div v-else class="tab-content tab-content--empty">
              <Typography variant="body-1" as="p"
                >No content available for this tab yet.</Typography
              >
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </div>
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import ProgramDetailColumn from "@/components/participant/programs/ProgramDetailColumn.vue";
import DOMPurify from "dompurify";

export default {
  name: "DynamicProgramDetailPage",
  components: { Typography, Button, ProgramDetailColumn },
  props: {
    programName: { type: String, default: "" },
    imageUrl: { type: String, default: null },
    dateRangeLabel: { type: String, default: "" },
    metricRows: { type: Array, default: () => [] },
    basis: { type: String, default: null },
    isDollarBudget: { type: Boolean, default: false },
    metricsLoading: { type: Boolean, default: false },
    metricsMissing: { type: Boolean, default: false },
    status: { type: String, default: null },
    metricsLoadFailed: { type: Boolean, default: false },
    tabs: { type: Array, default: () => [] },
    activeTabKey: { type: [String, Number], default: null },
    showSubmitClaim: { type: Boolean, default: false },
  },
  emits: ["submit-claim"],
  data() {
    return {
      activeTab: 0,
    };
  },
  watch: {
    activeTabKey: {
      immediate: true,
      handler() {
        const idx = this.tabs.findIndex((tab) => tab.key === this.activeTabKey);
        this.activeTab = idx >= 0 ? idx : 0;
      },
    },
  },
  methods: {
    sanitize(html) {
      return DOMPurify.sanitize(html);
    },
  },
};
</script>

<style scoped>
.detail-content {
  padding: 44px 80px 80px;
  text-align: left;
}

.program-title {
  margin-bottom: 24px;
  text-align: left;
}

.detail-body {
  display: flex;
  align-items: flex-start;
  gap: 35px;
}

.left-column {
  width: 411px;
  flex: 0 0 411px;
}

.right-column {
  background: white !important;
  border-radius: 8px !important;
  flex: 1 1 0;
  padding: 8px 24px 16px !important;
  box-shadow: 0px 2px 1px rgba(0, 0, 0, 0.14), 0px 3px 0.5px rgba(0, 0, 0, 0.12),
    0px 1px 2.5px rgba(0, 0, 0, 0.2) !important;
}

.submit-claim {
  display: flex;
  justify-content: center;
  padding: 8px 16px 16px;
}

.tabs {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.tabs-items {
  padding-top: 16px;
}

.tab-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
}

.tab-content--empty {
  color: rgba(0, 0, 0, 0.6);
}

.tab-content__body >>> p {
  margin: 0 0 16px;
  font-family: "Avenir", Helvetica, Arial, sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.87);
}

@media screen and (max-width: 959px) {
  .detail-content {
    padding: 16px 24px 24px;
  }

  .program-title {
    margin-bottom: 16px;
  }

  .detail-body {
    flex-direction: column;
    align-items: stretch;
    gap: 24px;
  }

  .left-column {
    width: 100%;
    flex: 1 1 auto;
  }

  .right-column {
    width: 100%;
  }
}
</style>
